import { UserAffinity } from '../types/menuConfig';

/** Elo de uma cadeia. `node` é o id de um item do menuConfig, de onde saem rota e permissão. */
export interface PrereqNode {
  node: string;
  /** Rótulo quando falta. O complemento de escopo varia por afinidade. */
  pending: Record<UserAffinity, string>;
  done: string;
  /** Versão curta para o card do mobile. */
  short?: string;
  /** Rótulo do botão que resolve este elo quando ele é o primeiro pendente. */
  actionLabel: string;
  /** Texto exibido no lugar do botão quando falta a permissão de criar este elo. */
  deniedHint: string;
}

export const PREREQ_NODES: Record<string, PrereqNode> = {
  providers: {
    node: 'providers',
    pending: {
      clinic: 'Nenhuma clínica cadastrada',
      company: 'Nenhum prestador vinculado a esta empresa',
      global: 'Nenhum prestador cadastrado no seu acesso',
    },
    done: 'Clínica cadastrada',
    short: 'Nenhuma clínica cadastrada',
    actionLabel: 'Cadastrar prestador',
    deniedHint: 'Peça ao administrador do sistema para cadastrar um prestador.',
  },
  professionals: {
    node: 'professionals',
    pending: {
      clinic: 'Nenhum profissional vinculado a esta clínica',
      company: 'Nenhum profissional disponível para esta empresa',
      global: 'Nenhum profissional cadastrado no seu acesso',
    },
    done: 'Profissional cadastrado',
    short: 'Nenhum profissional vinculado',
    actionLabel: 'Cadastrar profissional',
    deniedHint: 'Peça ao administrador da clínica para cadastrar um profissional.',
  },
  'professional-schedules': {
    node: 'professional-schedules',
    pending: {
      clinic: 'Nenhuma escala de atendimento criada nesta clínica',
      company: 'Nenhuma escala de atendimento disponível',
      global: 'Nenhuma escala de atendimento criada no seu acesso',
    },
    done: 'Escala de atendimento criada',
    short: 'Nenhuma escala criada',
    actionLabel: 'Criar escala de atendimento',
    deniedHint: 'Peça ao administrador da clínica para criar uma escala de atendimento.',
  },
  'schedule-slots': {
    node: 'schedule-slots',
    pending: {
      clinic: 'Nenhum horário gerado nesta clínica',
      company: 'Nenhum horário disponível para esta empresa',
      global: 'Nenhum horário gerado no seu acesso',
    },
    done: 'Horários gerados',
    short: 'Nenhum horário gerado',
    actionLabel: 'Gerar horários',
    deniedHint: 'Peça ao administrador da clínica para gerar os horários.',
  },
  departments: {
    node: 'departments',
    pending: {
      clinic: 'Nenhum setor cadastrado',
      company: 'Nenhum setor cadastrado nesta empresa',
      global: 'Nenhum setor cadastrado no seu acesso',
    },
    done: 'Setores cadastrados',
    short: 'Nenhum setor cadastrado',
    actionLabel: 'Cadastrar setor',
    deniedHint: 'Peça ao administrador da empresa para cadastrar um setor.',
  },
  occupations: {
    node: 'occupations',
    pending: {
      clinic: 'Nenhum cargo cadastrado',
      company: 'Nenhum cargo cadastrado nesta empresa',
      global: 'Nenhum cargo cadastrado no seu acesso',
    },
    done: 'Cargos cadastrados',
    short: 'Nenhum cargo cadastrado',
    actionLabel: 'Cadastrar cargo',
    deniedHint: 'Peça ao administrador da empresa para cadastrar um cargo.',
  },
  employees: {
    node: 'employees',
    pending: {
      clinic: 'Nenhum funcionário nas empresas que você atende',
      company: 'Nenhum funcionário nesta empresa',
      global: 'Nenhum funcionário no seu acesso',
    },
    done: 'Funcionários cadastrados',
    short: 'Nenhum funcionário cadastrado',
    actionLabel: 'Cadastrar funcionário',
    deniedHint: 'Peça ao administrador da empresa para cadastrar um funcionário.',
  },
  companies: {
    node: 'companies',
    pending: {
      clinic: 'Nenhuma empresa atendida por esta clínica',
      company: 'Nenhuma empresa no seu acesso',
      global: 'Nenhuma empresa cadastrada no seu acesso',
    },
    done: 'Empresa cadastrada',
    short: 'Nenhuma empresa cadastrada',
    actionLabel: 'Cadastrar empresa',
    deniedHint: 'Peça ao administrador do sistema para cadastrar uma empresa.',
  },
};

/** Cadeias por tela, na ordem em que a pessoa precisa resolvê-las. */
export const PREREQ_CHAINS: Record<string, string[]> = {
  'schedule-slots': ['providers', 'professionals', 'professional-schedules'],
  'professional-schedules': ['providers', 'professionals'],
  professionals: ['providers'],
  appointments: ['providers', 'professionals', 'professional-schedules', 'schedule-slots'],
  employees: ['companies', 'departments', 'occupations'],
  departments: ['companies'],
  occupations: ['companies'],
  units: ['companies'],
  /**
   * O painel da empresa. Ele só mede alguma coisa depois que existe funcionário, e funcionário
   * depende de setor e cargo — sem eles as filas ficam em zero por falta de cadastro, e não por
   * conformidade (#443, §6).
   */
  painel: ['companies', 'departments', 'occupations', 'employees'],
};
